import {
	Button,
	Icon,
	ImageCropSettings,
	Input,
	LibraryImage,
	Typography
} from 'components'
import { useDebounceEffect, useToast } from 'hooks'
import {
	Icons,
	ImageSlideProps,
	ItemDataAssentsAll,
	SlideImageFormInputs,
	TypographyTypes
} from 'interfaces'
import dayjs from 'lib/dayjs'
import React, { useEffect, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import {
	Crop,
	PixelCrop,
	ReactCrop,
	centerCrop,
	convertToPixelCrop,
	makeAspectCrop
} from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'

import {
	canvasPreview,
	configGenaral,
	generateTransform,
	regexVimeoOrYoutube,
	regexYoutubeVideoId
} from 'utils'

const ContentTab = (props: {
	label: string
	subLabel: string
	icon: Icons
	isActive: boolean
	onClick: () => void
}): JSX.Element => {
	const { label, subLabel, icon, isActive, onClick } = props

	return (
		<button
			type="button"
			className={`flex-1 flex items-center justify-center py-5 px-4 mx-1 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-sm gap-2 ${
				isActive
					? 'bg-blue-primary text-white shadow-lg scale-105'
					: 'bg-blue-50 text-blue-primary hover:bg-blue-100 border border-blue-200 hover:border-blue-300 hover:shadow-md'
			}`}
			onClick={onClick}
		>
			<Icon
				src={icon}
				className={`w-7 h-7 transition-all duration-200 ${
					isActive ? 'text-primary' : 'text-blue-primary'
				}`}
			/>
			<div className="flex flex-col items-center justify-center">
				<Typography
					fontLeading="12/15"
					type={TypographyTypes.h3}
					title={label}
					className={`font-bold text-xs text-center leading-tight ${
						isActive ? 'text-white' : 'text-blue-primary'
					}`}
				/>
				<Typography
					fontLeading="12/15"
					type={TypographyTypes.span}
					title={subLabel}
					className={`font-thin text-xs text-center leading-tight ${
						isActive ? 'text-white' : 'text-blue-primary'
					}`}
				/>
			</div>
		</button>
	)
}

const ImageReorderCard = (props: {
	image: ItemDataAssentsAll
	index: number
	total: number
	onMoveLeft: () => void
	onMoveRight: () => void
	onDelete: () => void
	isPreview?: boolean
}): JSX.Element => {
	const {
		image,
		index,
		total,
		onMoveLeft,
		onMoveRight,
		onDelete,
		isPreview = false
	} = props

	return (
		<div className="flex flex-col justify-center items-center gap-2">
			{/* Rounded Image Number Badge */}
			<div className="w-8 h-8 bg-blue-primary bg-opacity-90 rounded-full flex items-center justify-center shadow-sm">
				<span className="text-xs font-bold text-white">{index + 1}</span>
			</div>
			{/* Image Preview - Full card */}
			<div className="aspect-square bg-[#17B3C514] overflow-hidden rounded-lg flex items-center justify-center border border-blue-primary/10 drop-shadow-lg">
				<img
					src={image.asset_url}
					alt={`Preview ${index + 1}`}
					className="max-w-full max-h-full object-contain"
				/>
			</div>

			{/* Control Buttons - Only show if not preview */}
			{!isPreview && (
				<div className="flex items-center justify-between px-3 py-2 bg-blue-100 rounded-lg">
					<button
						type="button"
						onClick={onMoveLeft}
						disabled={index === 0}
						className={`p-1.5 rounded-md transition-all ${
							index === 0
								? 'text-gray-400 cursor-not-allowed'
								: 'text-gray-700 hover:text-black cursor-pointer'
						}`}
					>
						<Icon src={Icons.arrowCircleLeft} className="w-4 h-4" />
					</button>

					<button
						type="button"
						onClick={onDelete}
						className="p-1.5 rounded-md cursor-pointer"
					>
						<Icon src={Icons.deleteXblue} className="w-4 h-4 " />
					</button>

					<button
						type="button"
						onClick={onMoveRight}
						disabled={index === total - 1}
						className={`p-1.5 rounded-md transition-all ${
							index === total - 1
								? 'text-gray-400 cursor-not-allowed'
								: 'text-gray-700 hover:text-black cursor-pointer'
						}`}
					>
						<Icon src={Icons.arrowCircleRight} className="w-4 h-4" />
					</button>
				</div>
			)}
		</div>
	)
}

ImageReorderCard.defaultProps = {
	isPreview: false
}

const getYoutubeThumbnail = (url: string): string => {
	const videoId = url.match(regexYoutubeVideoId)
	return videoId
		? `https://img.youtube.com/vi/${videoId[1]}/maxresdefault.jpg`
		: ''
}

const getVideoTitle = (url: string): string => {
	if (url.includes('youtube.com') || url.includes('youtu.be')) {
		return 'YouTube Video'
	}
	if (url.includes('vimeo.com')) {
		return 'Vimeo Video'
	}
	return 'Video'
}

const VideoCard = (props: {
	videoUrl: string
	onDelete: () => void
	isPreview?: boolean
}): JSX.Element => {
	const { videoUrl, onDelete, isPreview = false } = props

	const isYoutube =
		videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')
	const isVimeo = videoUrl.includes('vimeo.com')

	const [vimeoThumbnail, setVimeoThumbnail] = useState<string>('')

	useEffect(() => {
		const controller = new AbortController()
		if (isVimeo) {
			setVimeoThumbnail('')
			fetch(
				`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(videoUrl)}`,
				{ signal: controller.signal }
			)
				.then(res => {
					if (!res.ok) throw new Error(`oEmbed error: ${res.status}`)
					return res.json()
				})
				.then((data: { thumbnail_url?: string }) => {
					if (data?.thumbnail_url) {
						setVimeoThumbnail(data.thumbnail_url)
					}
				})
				.catch(() => {
					setVimeoThumbnail('')
				})
		}
		return () => controller.abort()
	}, [videoUrl, isVimeo])

	const thumbnail = isYoutube ? getYoutubeThumbnail(videoUrl) : vimeoThumbnail
	const title = getVideoTitle(videoUrl)

	return (
		<div className="flex flex-col justify-center items-center gap-2">
			{/* Rounded Video Badge */}
			<div className="w-8 h-8 bg-red-500 bg-opacity-90 rounded-full flex items-center justify-center shadow-sm">
				<Icon src={Icons.playArrow} className="w-3 h-3 text-white" />
			</div>
			{/* Video Preview - Full card */}
			<div className="aspect-square bg-gray-100 overflow-hidden rounded-lg relative">
				{thumbnail ? (
					<img
						src={thumbnail}
						alt="Video thumbnail"
						className="w-full h-full object-cover"
					/>
				) : (
					<div className="flex flex-col items-center justify-center text-gray-500 h-full">
						<Icon src={Icons.playArrow} className="w-12 h-12 mb-2" />
						<Typography
							fontLeading="12/15"
							type={TypographyTypes.span}
							title={title}
							className="text-xs text-center font-medium"
						/>
					</div>
				)}

				{/* Play icon overlay */}
				<div className="absolute inset-0 flex items-center justify-center">
					<div className="bg-black bg-opacity-50 rounded-full p-2">
						<Icon src={Icons.playArrow} className="w-6 h-6 text-white" />
					</div>
				</div>
			</div>

			{/* Control Buttons - Only show if not preview */}
			{!isPreview && (
				<div className="flex items-center justify-between px-3 py-2 bg-blue-100 rounded-lg">
					<button
						type="button"
						onClick={onDelete}
						className="p-1.5 rounded-md text-red-500 hover:text-red-700 hover:scale-105 transition-all"
					>
						<Icon src={Icons.delete} className="w-4 h-4 text-red-500" />
					</button>
				</div>
			)}
		</div>
	)
}

VideoCard.defaultProps = {
	isPreview: false
}

const ImagePreviewCard = (props: {
	image: ItemDataAssentsAll
	index: number
}): JSX.Element => {
	const { image, index } = props

	return (
		<ImageReorderCard
			image={image}
			index={index}
			total={0}
			/* eslint-disable-next-line @typescript-eslint/no-empty-function */
			onMoveLeft={(): void => {}}
			/* eslint-disable-next-line @typescript-eslint/no-empty-function */
			onMoveRight={(): void => {}}
			/* eslint-disable-next-line @typescript-eslint/no-empty-function */
			onDelete={(): void => {}}
			isPreview
		/>
	)
}

const VideoPreviewCard = (props: { videoUrl: string }): JSX.Element => {
	const { videoUrl } = props

	return (
		<VideoCard
			videoUrl={videoUrl}
			/* eslint-disable-next-line @typescript-eslint/no-empty-function */
			onDelete={(): void => {}}
			isPreview
		/>
	)
}

const UploadMoreCard = (props: {
	onClick: () => void
	disabled: boolean
}): JSX.Element => {
	const { onClick, disabled } = props

	return (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			className={`bg-blue-50 aspect-square mt-10 rounded-lg shadow-md overflow-hidden border-2 transition-all duration-300 hover:scale-105 ${
				disabled
					? 'border-gray-300 bg-gray-50 cursor-not-allowed'
					: 'border-blue-primary'
			}`}
		>
			{/* Upload Icon Area */}
			<div className="aspect-square relative overflow-hidden flex flex-col items-center justify-center">
				<div
					className={`p-3 rounded-full mb-2 ${
						disabled ? 'bg-gray-200' : 'bg-blue-100'
					}`}
				>
					<Icon
						src={Icons.uploadCloud}
						className={`w-8 h-8 ${
							disabled ? 'text-gray-400' : 'text-blue-primary'
						}`}
					/>
				</div>
				<div className="flex flex-col items-center justify-center">
					<Typography
						fontLeading="12/15"
						type={TypographyTypes.span}
						title="Upload "
						className={`font-semibold text-xs text-center ${
							disabled ? 'text-gray-400' : 'text-blue-primary'
						}`}
					/>
					<Typography
						fontLeading="12/15"
						type={TypographyTypes.span}
						title=" Images"
						className={`font-extralight text-xs text-center ${
							disabled ? 'text-gray-400' : 'text-blue-primary'
						}`}
					/>
				</div>
			</div>

			{disabled && (
				<div className="p-2 bg-gray-50">
					<Typography
						fontLeading="10/12"
						type={TypographyTypes.span}
						title="Max 5 images"
						className="text-xs text-center"
					/>
				</div>
			)}
		</button>
	)
}

export const Image: React.FC<ImageSlideProps> = ({
	totalData,
	listImage,
	setTotalData,
	setOpenSlide,
	setListImage,
	setSlideImages,
	setSlideLibraryImages,
	mode,
	typeFunction = 'Content',
	isCropSettingsVisible,
	setIsCropSettingsVisible,
	setSendImageCropped,
	sendImageCropped,
	setIsFinishCropButtonVisible,
	isFinishCropButtonVisible,
	setIsCropComplete,
	isCropComplete,
	setVideoId,
	setValueInput,
	valueInput
}): JSX.Element => {
	const { showToast } = useToast()
	const previewCanvasRef = useRef<HTMLCanvasElement | null>(null)
	const imgRef = useRef<HTMLImageElement>(null)
	const [crop, setCrop] = useState<Crop | null>()
	const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>()
	const [scale, setScale] = useState(1)
	const [rotate, setRotate] = useState(0)
	const [flipVertical, setFlipVertical] = useState(false)
	const [flipHorizontal, setFlipHorizontal] = useState(false)
	const [aspectString, setAspectString] = useState<string | undefined>()
	const [isActiveFinish, setIsActiveFinish] = useState(false)
	const [isCropped, setIsCropped] = useState(true)
	const [valueInputUrlVideo, setValueInputUrlVideo] = useState('')
	const [isValidatingVideo, setIsValidatingVideo] = useState(false)
	const [isDragOver, setIsDragOver] = useState(false)
	const [activeTab, setActiveTab] = useState<'upload' | 'library' | 'video'>(
		'upload'
	)

	const {
		register,
		setValue,
		handleSubmit,
		formState: { errors }
	} = useForm<SlideImageFormInputs>({
		mode: 'all'
	})

	const rules = {
		url: {
			maxLength: { value: 150, message: 'Maximum characters exceeded' },
			pattern: {
				value: regexVimeoOrYoutube,
				message: 'Invalid link'
			}
		}
	}

	useEffect(() => {
		if (
			valueInputUrlVideo &&
			setVideoId &&
			regexVimeoOrYoutube.test(valueInputUrlVideo)
		) {
			setVideoId(valueInputUrlVideo)
		} else {
			setVideoId?.('')
		}
	}, [valueInputUrlVideo])

	useEffect(() => {
		if (valueInput && setValueInput && regexVimeoOrYoutube.test(valueInput)) {
			setValueInput(valueInput)
		}
	}, [valueInput])

	const isAssetCropped =
		listImage[listImage.length - 1]?.asset_url?.includes('.amazonaws.com/')

	const registerInputFile = register('image')

	const resetCropState = (): void => {
		setCrop(null)
		setCompletedCrop(null)
		setIsCropSettingsVisible?.(true)
		setIsFinishCropButtonVisible(true)
		setIsCropComplete(false)
		setScale(1)
		setRotate(0)
		setFlipVertical(false)
		setFlipHorizontal(false)
		setIsActiveFinish(false)
	}

	const handleFinishCrop = (): void => {
		if (completedCrop && previewCanvasRef.current) {
			const canvas = previewCanvasRef.current
			const resizedCanvas = document.createElement('canvas')
			const resizedCtx = resizedCanvas.getContext('2d')

			if (aspectString === '1/1') {
				resizedCanvas.width = 1080
				resizedCanvas.height = 1080
				if (resizedCtx !== null) {
					resizedCtx.imageSmoothingEnabled = false
					resizedCtx.drawImage(canvas, 0, 0, 1080, 1080)
				}
			} else if (aspectString === '1/1.2941') {
				resizedCanvas.width = 1080
				resizedCanvas.height = 1398
				if (resizedCtx !== null) {
					resizedCtx.imageSmoothingEnabled = false
					resizedCtx.drawImage(canvas, 0, 0, 1080, 1398)
				}
			} else {
				resizedCanvas.width = 1920
				resizedCanvas.height = 1080
				if (resizedCtx !== null) {
					resizedCtx.imageSmoothingEnabled = false
					resizedCtx.drawImage(canvas, 0, 0, 1920, 1080)
				}
			}

			const resizedImageData = resizedCanvas.toDataURL('image/png')

			const updatedListImage = [...listImage]
			updatedListImage[updatedListImage.length - 1].asset_url = resizedImageData

			const newImage = {
				asset_type: 'image',
				asset_url: resizedImageData,
				asset_name: `${dayjs().format('YYYYMMDDHHmmss')}-image`,
				asset_aspect: aspectString
			}

			setSendImageCropped(prevList => [...prevList, newImage])
			setIsFinishCropButtonVisible(false)
			setIsCropSettingsVisible?.(false)
			setIsCropComplete(true)
			setIsCropped(true)
			setRotate(0)
			setScale(1)
		}
	}

	const handleUploadImage = (file: File): void => {
		const fileSize = file.size
		const fileMb = fileSize / 1024 ** 2
		if (fileMb < configGenaral.maxfileSizeMB) {
			const render = new FileReader()
			render.onload = e => {
				const uploadedObj = { file, url: e.target?.result as string }
				setListImage([
					...listImage,
					{
						asset_type: 'image',
						asset_url: uploadedObj.url,
						asset_name: `${dayjs().format('YYYYMMDDHHmmss')}-image`
					}
				])
				setAspectString(undefined)
			}
			render.readAsDataURL(file)
		} else {
			showToast(
				'Error',
				`Please select a file less than ${configGenaral.maxfileSizeMB}MB.`,
				'error'
			)
			setIsCropped(true)
		}
		resetCropState()
	}

	// Drag and drop handlers
	const handleDragEnter = (e: React.DragEvent): void => {
		e.preventDefault()
		e.stopPropagation()
		setIsDragOver(true)
	}

	const handleDragLeave = (e: React.DragEvent): void => {
		e.preventDefault()
		e.stopPropagation()
		setIsDragOver(false)
	}

	const handleDragOver = (e: React.DragEvent): void => {
		e.preventDefault()
		e.stopPropagation()
	}

	const handleDrop = (e: React.DragEvent): void => {
		e.preventDefault()
		e.stopPropagation()
		setIsDragOver(false)

		const { files } = e.dataTransfer
		if (files.length > 0 && listImage.length < 5) {
			const file = files[0]
			// Check if it's an image file
			if (file.type.startsWith('image/')) {
				setIsCropped(false)
				handleUploadImage(file)
			} else {
				showToast('Error', 'Please upload only image files.', 'error')
			}
		}
	}

	useEffect(() => {
		if (isAssetCropped) {
			setIsCropComplete(true)
		}
	}, [isAssetCropped])

	useEffect(() => {
		if (valueInputUrlVideo) {
			setIsCropComplete(true)
		}
	}, [valueInputUrlVideo])

	// Set isCropComplete to true when images are picked from library or when there are existing images
	useEffect(() => {
		if (listImage.length > 0 && activeTab === 'library') {
			setIsCropComplete(true)
		}
	}, [listImage, activeTab])

	useDebounceEffect(
		() => {
			const generateCanvasPreview = async (): Promise<void> => {
				if (
					completedCrop?.width &&
					completedCrop?.height &&
					imgRef.current &&
					previewCanvasRef.current
				) {
					await canvasPreview(
						imgRef.current,
						previewCanvasRef.current,
						completedCrop,
						scale,
						rotate
						// flipVertical,
						// flipHorizontal
					)
				}
			}

			generateCanvasPreview()
		},
		500,
		[completedCrop, scale, rotate, flipVertical, flipHorizontal]
	)

	const handleDeleteImage = (id: number): void => {
		const newListImage = listImage
		newListImage.splice(id, 1)
		setListImage([...newListImage])
		setSendImageCropped([...newListImage])
		setIsCropped(true)
		setAspectString(undefined)
		resetCropState()
		const input = document.getElementById('image') as HTMLInputElement
		if (input) {
			input.value = ''
		}
	}

	const handleDeleteVideo = (): void => {
		setValueInputUrlVideo('')
		setValueInput?.('')
		setVideoId?.('')
	}

	const moveImageLeft = (index: number): void => {
		if (index > 0) {
			const newListImage = [...listImage]
			const temp = newListImage[index]
			newListImage[index] = newListImage[index - 1]
			newListImage[index - 1] = temp
			setListImage(newListImage)
			setSendImageCropped(newListImage)
		}
	}

	const moveImageRight = (index: number): void => {
		if (index < listImage.length - 1) {
			const newListImage = [...listImage]
			const temp = newListImage[index]
			newListImage[index] = newListImage[index + 1]
			newListImage[index + 1] = temp
			setListImage(newListImage)
			setSendImageCropped(newListImage)
		}
	}

	const onSubmit: SubmitHandler<SlideImageFormInputs> = async () => {
		if (
			sendImageCropped !== undefined &&
			sendImageCropped !== null &&
			sendImageCropped.length > 0
		) {
			setTotalData({
				...totalData,
				basic_setup: {
					...totalData.basic_setup,
					preview: {
						...totalData.basic_setup.preview,
						files: sendImageCropped,
						video_link_external: valueInputUrlVideo || (valueInput as string)
					}
				}
			})
			setOpenSlide(false)
			setSlideImages(false)
		} else {
			setTotalData({
				...totalData,
				basic_setup: {
					...totalData.basic_setup,
					preview: {
						...totalData.basic_setup.preview,
						files: [],
						video_link_external: valueInputUrlVideo || (valueInput as string)
					}
				}
			})
			setOpenSlide(false)
			setSlideImages(false)
		}
	}

	const centerAspectCrop = (
		mediaWidth: number,
		mediaHeight: number,
		aspectt: number
	): Crop => {
		return centerCrop(
			makeAspectCrop(
				{
					unit: '%',
					width: 90
				},
				aspectt,
				mediaWidth,
				mediaHeight
			),
			mediaWidth,
			mediaHeight
		)
	}

	const handleToggleAspectClick = (
		aspect: number,
		aspectStringg: string
	): void => {
		setAspectString(aspectStringg)
		if (imgRef.current) {
			const { width, height } = imgRef.current
			const newCrop = centerAspectCrop(width, height, aspect)

			setCrop(newCrop)
			// Updates the preview
			setCompletedCrop(convertToPixelCrop(newCrop, width, height))
		}
	}

	const handleControlZoom = (): void => {
		if (completedCrop) {
			if (scale <= 0.5) {
				setScale(0.5)
			}
		}
	}

	const resetCrop = (): void => {
		setCrop(null)
		setCompletedCrop(null)
	}

	useEffect(() => {
		if (totalData?.basic_setup?.preview) {
			const videoLinkExternal =
				totalData.basic_setup.preview.video_link_external
			if (videoLinkExternal) {
				setValue('url', videoLinkExternal)
				setValueInputUrlVideo(videoLinkExternal)
				setValueInput?.(videoLinkExternal)
			}
		}
	}, [totalData])

	const cleanYouTubeUrl = (url: string): string => {
		if (!url.trim()) {
			return ''
		}
		try {
			const urlObj = new URL(url)
			if (
				urlObj.hostname.includes('youtube.com') ||
				urlObj.hostname.includes('youtu.be')
			) {
				const videoId = urlObj.searchParams.get('v')
				showToast(
					'Information',
					'We have optimized your link for the platform',
					'warning'
				)
				if (videoId) {
					return `https://www.youtube.com/watch?v=${videoId}`
				}
			}
			return url
		} catch (error) {
			return url
		}
	}

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			className="h-full flex flex-col justify-between w-full"
		>
			<div>
				{/* Tab Navigation */}
				<div className="flex p-3 rounded-lg mb-6 gap-2 mt-2">
					<ContentTab
						label="Upload Images"
						subLabel="From your device"
						icon={
							activeTab === 'upload'
								? Icons.uploadFromDevice
								: Icons.uploadFromDeviceBlue
						}
						isActive={activeTab === 'upload'}
						onClick={() => setActiveTab('upload')}
					/>
					<ContentTab
						label="Browse Images"
						subLabel="From library"
						icon={activeTab === 'library' ? Icons.library : Icons.libraryBlue}
						isActive={activeTab === 'library'}
						onClick={() => setActiveTab('library')}
					/>
					<ContentTab
						label="Add Video"
						subLabel="From link"
						icon={activeTab === 'video' ? Icons.videoLink : Icons.videoLinkBlue}
						isActive={activeTab === 'video'}
						onClick={() => setActiveTab('video')}
					/>
				</div>
			</div>
			<div className="flex flex-1 relative flex-col justify-center items-center border-2 border-blue-primary rounded-xl p-4 w-full h-full">
				{/* Tab Content */}
				{activeTab === 'upload' && (
					<div>
						{/* Hidden file input for uploads */}
						<input
							id="image"
							type="file"
							className="hidden"
							{...registerInputFile}
							onChange={event => {
								if (event.target.files && event.target.files.length !== 0) {
									setIsCropped(false)
									handleUploadImage(event.target.files[0])
								} else {
									setIsCropped(true)
								}
							}}
							accept="image/png, image/jpeg, image/jpg"
						/>

						{/* Cropping Interface - only show when cropping */}
						{listImage.length > 0 && !isCropped && (
							<div className="my-4 relative">
								<div className="relative">
									<div className="flex justify-center items-center">
										<ReactCrop
											crop={crop as Crop}
											onChange={(_, percentCrop) => {
												setCrop(percentCrop)
												setIsActiveFinish(false)
											}}
											onComplete={c => {
												setCompletedCrop(c)
												setIsActiveFinish(true)
											}}
											locked
										>
											<img
												src={listImage[listImage.length - 1].asset_url}
												alt="preview content"
												className="!max-h-[20rem]"
												style={{
													transform: generateTransform(
														scale,
														rotate,
														flipHorizontal,
														flipVertical
													)
												}}
												ref={imgRef}
											/>
										</ReactCrop>
									</div>
									{isCropSettingsVisible &&
										!isAssetCropped &&
										(mode === 'edit' || mode === 'create') && (
											<ImageCropSettings
												handleToggleAspectClick={handleToggleAspectClick}
												rotate={rotate}
												setRotate={setRotate}
												scale={scale}
												setScale={setScale}
												handleControlZoom={handleControlZoom}
												completedCrop={completedCrop as PixelCrop}
												aspectString={aspectString}
											/>
										)}
									{isFinishCropButtonVisible && !isAssetCropped && (
										<div className="pt-2">
											<Button
												label="Finish Crop"
												type="button"
												color="Primary"
												className="w-full mt-1"
												onClick={() => {
													handleFinishCrop()
													setIsFinishCropButtonVisible(false)
													setIsCropSettingsVisible?.(false)
													resetCrop()
													setIsCropComplete(true)
												}}
												disabled={!isActiveFinish}
											/>
										</div>
									)}
									{!!completedCrop && (
										<canvas
											id="croppedCanvas"
											className=" h-0 w-0 rounded-lg border-blue-300"
											ref={previewCanvasRef}
											style={{
												visibility: 'hidden',
												border: '1px solid black',
												objectFit: 'contain'
											}}
										/>
									)}
									<button
										type="button"
										onClick={() => handleDeleteImage(-1)}
										className="absolute -top-1 -right-1 bg-gray-18 p-1 rounded-full border-2 border-gray-3"
									>
										<Icon src={Icons.close} className="w-3 h-3 " />
									</button>
								</div>
							</div>
						)}

						{/* Media Management Gallery - Hidden during cropping */}
						{isCropped && (
							<div className="mt-4">
								{/* Instructions at the top */}
								{listImage.length > 0 && (
									<div className="flex flex-col gap-2 absolute top-0 left-0 right-0 mt-2 p-4">
										<Typography
											fontLeading="16/18"
											type={TypographyTypes.h3}
											title="Organize and Upload Images"
											className="font-semibold  text-left"
										/>
										<Typography
											fontLeading="15/15"
											type={TypographyTypes.h3}
											title="Click the arrow buttons on each image to reorder them. Click 'Upload Images' to add more."
											className="text-left font-light text-gray-500"
										/>
									</div>
								)}
								{/* Unified Drag and Drop Container */}
								<div
									className={`relative border-2 border-dashed rounded-lg p-6 transition-all duration-200 ${
										isDragOver
											? 'border-blue-primary bg-blue-50'
											: 'border-gray-300 hover:border-gray-400'
									}`}
									onDragEnter={handleDragEnter}
									onDragLeave={handleDragLeave}
									onDragOver={handleDragOver}
									onDrop={handleDrop}
								>
									{/* Empty State */}
									{listImage.length === 0 && !valueInputUrlVideo ? (
										<div className="text-center py-8">
											<Typography
												fontLeading="15/18"
												type={TypographyTypes.h3}
												title="Drag and drop an image here"
												className="text-gray-600 font-semibold mb-2"
											/>
											<Typography
												fontLeading="12/15"
												type={TypographyTypes.p}
												title="Or click the button below to upload an image from your device"
												className="text-gray-500 mb-4"
											/>
											<button
												type="button"
												onClick={() => {
													const input = document.getElementById('image')
													if (input) input.click()
												}}
												className="bg-blue-primary text-white px-6 py-1 rounded-full font-semibold shadow-md cursor-pointer"
											>
												Upload Image
											</button>
										</div>
									) : (
										/* Cards Grid */
										<div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
											{/* Existing Image Cards */}
											{listImage.map((image, index) => (
												<ImageReorderCard
													key={`${image.asset_url}-${index}`}
													image={image}
													index={index}
													total={listImage.length}
													onMoveLeft={() => moveImageLeft(index)}
													onMoveRight={() => moveImageRight(index)}
													onDelete={() => handleDeleteImage(index)}
												/>
											))}

											{/* Video Card */}
											{valueInputUrlVideo &&
												regexVimeoOrYoutube.test(valueInputUrlVideo) && (
													<VideoCard
														videoUrl={valueInputUrlVideo}
														onDelete={() => {
															handleDeleteVideo()
															setValue('url', '')
														}}
													/>
												)}

											{/* Upload More Card */}
											<UploadMoreCard
												onClick={() => {
													const input = document.getElementById('image')
													if (input) input.click()
												}}
												disabled={listImage.length >= 5 || !isCropped}
											/>
										</div>
									)}
								</div>
							</div>
						)}
					</div>
				)}

				{/* Video Tab Content */}
				{activeTab === 'video' && (
					<div className="pt-4 w-full h-full flex flex-col justify-center items-center">
						<div className="w-full mb-4 flex flex-col gap-2 justify-center items-center">
							<Typography
								fontLeading="16/18"
								type={TypographyTypes.h3}
								title="Link to Your Video"
								className="font-semibold text-left"
							/>
							<Typography
								fontLeading="15/15"
								type={TypographyTypes.h3}
								title="Copy a link to your YouTube or Vimeo video and paste it below"
								className="text-left font-light text-gray-500"
							/>
						</div>
						<Input
							name="url"
							rules={rules.url}
							error={errors.url}
							borderFull={false}
							register={register}
							setValueInput={setValue}
							inputClassName="rounded"
							placeholder="Paste your YouTube or Vimeo link here"
							onChangeCustom={e => {
								const inputValue = e.target.value
								if (!inputValue.trim()) {
									setValueInputUrlVideo('')
									setValueInput?.('')
									return
								}
								try {
									const cleanURl = cleanYouTubeUrl(inputValue)
									setValueInputUrlVideo(cleanURl)
									setValueInput?.(cleanURl)
								} catch (error) {
									setValueInputUrlVideo(inputValue)
									setValueInput?.(inputValue)
								}
							}}
							value={valueInput}
						/>
						{/* Video Tab Navigation Buttons */}
						<div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 w-full">
							<Button
								type="button"
								label="Cancel"
								color="Transparent"
								className="!min-w-[130px]"
								onClick={() => {
									setValueInputUrlVideo('')
									setValueInput?.('')
									setActiveTab('upload')
								}}
							/>
							<Button
								type="button"
								label={isValidatingVideo ? 'Validating...' : 'Continue'}
								color="Primary"
								className="!min-w-[130px]"
								disabled={
									!valueInputUrlVideo ||
									!regexVimeoOrYoutube.test(valueInputUrlVideo) ||
									isValidatingVideo
								}
								onClick={() => {
									const isVimeoUrl = valueInputUrlVideo.includes('vimeo.com')
									if (!isVimeoUrl) {
										setActiveTab('upload')
										showToast(
											'Success',
											'Video added successfully! You can continue adding images.',
											'success'
										)
										return
									}
									setIsValidatingVideo(true)
									const validationController = new AbortController()
									const timeoutId = setTimeout(
										() => validationController.abort(),
										10000
									)
									fetch(
										`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(
											valueInputUrlVideo
										)}`,
										{ signal: validationController.signal }
									)
										.then(res => {
											if (!res.ok) throw new Error('invalid')
											return res.json()
										})
										.then(
											(data: {
												thumbnail_url?: string
												domain_status_code?: number
											}) => {
												if (
													data.domain_status_code === 403 ||
													!data.thumbnail_url
												) {
													showToast(
														'Error',
														'This Vimeo video cannot be added. Please check the video privacy settings or use a publicly accessible video.',
														'error'
													)
												} else {
													setActiveTab('upload')
													setValue('url', '')
													setValueInput?.('')
													showToast(
														'Success',
														'Video added successfully! You can continue adding images.',
														'success'
													)
												}
											}
										)
										.catch(() => {
											showToast(
												'Error',
												'Could not validate the Vimeo video. Please try again.',
												'error'
											)
										})
										.finally(() => {
											clearTimeout(timeoutId)
											setIsValidatingVideo(false)
										})
								}}
							/>
						</div>
					</div>
				)}

				{/* Library Tab Content */}
				{activeTab === 'library' && (
					<div className="pt-4  ">
						<div className="overflow-y-hidden library-inline w-full h-full max-h-[60vh]">
							<style>
								{`.library-inline .flex.items-center.justify-between.mt-4 { display: none !important; }`}
							</style>
							<LibraryImage
								setSendImageCropped={setSendImageCropped}
								listImage={listImage}
								setOpenSlide={setOpenSlide}
								setListImage={setListImage}
								setSlideImages={setSlideImages}
								setSlideLibraryImages={setSlideLibraryImages}
								libaryType={
									typeFunction === 'Content' ? 'contents' : 'locations'
								}
							/>
						</div>
					</div>
				)}
			</div>
			<div className="flex items-center justify-between mt-4">
				<Button
					type="button"
					label="Cancel"
					color="Transparent"
					className="!min-w-[130px]"
					onClick={() => {
						setOpenSlide(false)
						setSlideImages(false)
					}}
				/>
				<Button
					label="Save"
					type="submit"
					color="Primary"
					className="!min-w-[130px]"
					disabled={
						(listImage.length === 0 &&
							valueInputUrlVideo === '' &&
							valueInput === '') ||
						(!isCropComplete &&
							activeTab === 'upload' &&
							listImage.length > 0 &&
							!isAssetCropped)
					}
				/>
			</div>
		</form>
	)
}

// Media Preview Gallery for main content area
export const MediaPreviewGallery = (props: {
	listImage: ItemDataAssentsAll[]
	videoUrl?: string
	onClickEdit: () => void
}): JSX.Element => {
	const { listImage, videoUrl = '', onClickEdit } = props

	const hasMedia =
		listImage.length > 0 || (videoUrl && regexVimeoOrYoutube.test(videoUrl))

	if (!hasMedia) {
		return (
			<div className="border-blue-primary border-solid border-[2px] rounded-xl min-w-full">
				<div className="bg-gray-11 px-[61px] py-36 w-full h-[528px] rounded-xl">
					<button
						type="button"
						onClick={onClickEdit}
						className="flex items-center justify-center py-3 bg-white shadow-boxPreview rounded w-full"
					>
						<div>
							<Typography
								fontLeading="15/18"
								type={TypographyTypes.p}
								title="Click here to add"
								className="!font-normal text-lavender"
							/>
							<Typography
								fontLeading="15/22"
								type={TypographyTypes.p}
								title="Images or Videos"
								className="!font-semibold text-lavender"
							/>
						</div>
						<Icon src={Icons.uploadFromDevice} className="w-[26px] pl-2" />
					</button>
				</div>
			</div>
		)
	}

	return (
		<div className="border-blue-primary border-solid border-[2px] rounded-xl min-w-full p-6">
			<div className="flex items-center justify-between mb-4">
				<Typography
					fontLeading="15/18"
					type={TypographyTypes.h3}
					title="Media Preview"
					className="text-gray-800 font-semibold"
				/>
				<Button
					type="button"
					label="Edit Media"
					color="Transparent"
					className="!min-w-[120px] text-sm"
					onClick={onClickEdit}
				/>
			</div>

			<div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
				{/* Image Preview Cards */}
				{listImage.map((image, index) => (
					<ImagePreviewCard
						key={`${image.asset_url}-${index}`}
						image={image}
						index={index}
					/>
				))}

				{/* Video Preview Card */}
				{videoUrl && regexVimeoOrYoutube.test(videoUrl) && (
					<VideoPreviewCard videoUrl={videoUrl} />
				)}
			</div>

			<div className="mt-4 text-center">
				<Typography
					fontLeading="12/15"
					type={TypographyTypes.p}
					title={`${listImage.length} image${
						listImage.length !== 1 ? 's' : ''
					}${
						videoUrl && regexVimeoOrYoutube.test(videoUrl) ? ' and 1 video' : ''
					} uploaded`}
					className="text-gray-600 text-sm"
				/>
			</div>
		</div>
	)
}

MediaPreviewGallery.defaultProps = {
	videoUrl: ''
}

// Export preview components for use in main content area
export { ImagePreviewCard, VideoPreviewCard }
